import { IndividualLearningPlans } from '@/pages/learn/study/IndividualLearningPlans';
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LmsAssignmentResponse } from '@/pages/learn/lms/LmsAssignmentResponse';
import { SchoolLearningPage } from '@/pages/portal/SchoolLearningPage';
import { createLmsDemoApi } from './demoLmsApi';
export function LmsDemoPage() {
  const { kind, perspective } = useParams(),
    [search, setSearch] = useSearchParams();
  const state = ['draft', 'submitted', 'returned', 'reviewed'].includes(search.get('state') ?? '')
    ? search.get('state')!
    : 'reviewed';
  const practice = search.get('practice') === '1';
  const school = search.get('school');
  const profile = kind === 'tutor' ? 'tutor' : 'school';
  const request = useMemo(
    () => createLmsDemoApi(profile, state, practice, kind === 'coco'),
    [profile, state, practice, kind],
  );
  if (
    (kind !== 'school' && kind !== 'tutor' && kind !== 'coco') ||
    (perspective !== 'student' && perspective !== 'family')
  )
    return (
      <main className="mx-auto max-w-3xl p-6">
        <h1 className="section-heading">Choose a learning demo</h1>
        <Link className="btn-pill-primary" to="/try/lms/school/family">
          Open school example
        </Link>
      </main>
    );
  const organisation =
    kind === 'coco' ? 'Coco' : profile === 'school' ? 'Brightwood Learning' : 'Willow Tutoring';
  const formalPath =
    perspective === 'family'
      ? '/portal/school-learning'
      : `/learn/study${school ? `?school=${encodeURIComponent(school)}` : ''}`;
  const teacherOrigin = import.meta.env.VITE_INSTITUTION_PORTAL_URL?.replace(/\/$/, '');
  return (
    <div className="min-h-dvh bg-canvas pb-28">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-[70] rounded-lg border-[3px] border-brand-sky"
      />
      <header className="sticky top-0 z-30 border-b border-brand-sky/30 bg-wash-sky px-4 py-3">
        <div className="mx-auto flex max-w-5xl items-center gap-3">
          <img
            src={`${import.meta.env.BASE_URL}demo/${profile === 'school' ? 'brightwood' : 'willow'}-logo.svg`}
            alt={`${organisation} fictional demo logo`}
            className="h-10 w-10 shrink-0 rounded-xl"
          />
          <div>
            <p className="text-sm font-bold text-ink">{organisation} · Demo mode</p>
            <p className="text-xs text-slate2">
              Fictional data · {practice ? 'Local practice in memory' : 'Read-only result preview'}
            </p>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl space-y-5 px-4 py-5 sm:px-6">
        <section
          aria-label="LMS demo notice"
          className="rounded-2xl border border-hairline bg-canvas-pure p-4 sm:p-5"
        >
          <h1 className="text-xl font-bold text-ink">
            {perspective === 'family' ? 'The family’s view' : 'The student’s view'}
          </h1>
          <p className="mt-2 text-sm text-slate2">
            Follow Alex’s worked explanation, the teacher’s feedback and the published family
            report. These are separate prepared examples; local practice does not update the teacher
            or family view.
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            {perspective === 'student' && (
              <label className="grid gap-1 text-xs font-semibold text-slate2">
                Example stage
                <select
                  className="input-k12 !py-2 !text-sm"
                  value={state}
                  onChange={(event) =>
                    setSearch({
                      state: event.target.value,
                      ...(practice ? { practice: '1' } : {}),
                      ...(school ? { school } : {}),
                    })
                  }
                >
                  <option value="draft">Private draft</option>
                  <option value="submitted">Awaiting review</option>
                  <option value="returned">Returned for revision</option>
                  <option value="reviewed">Published feedback</option>
                </select>
              </label>
            )}
            {!practice && (
              <Link
                className="btn-pill-secondary !py-2 !text-sm"
                to={
                  perspective === 'student'
                    ? `/try/lms/${kind === 'coco' ? 'coco' : profile}/student?state=draft&practice=1${school ? `&school=${encodeURIComponent(school)}` : ''}`
                    : `/try/lms/${profile}/family?practice=1`
                }
              >
                {perspective === 'student'
                  ? 'Try a response locally'
                  : 'Try notice acknowledgement'}
              </Link>
            )}
            {practice && (
              <button
                className="btn-pill-secondary !py-2 !text-sm"
                onClick={() => window.location.reload()}
              >
                Reset this demo
              </button>
            )}
          </div>
          <details className="mt-3 text-xs text-slate2">
            <summary className="cursor-pointer font-semibold text-ink">
              About this example and other views
            </summary>
            <p className="mt-2">
              No real submission, message, payment or AI usage occurs. Text and the bundled project
              can be practised in memory; image uploads and real guardian invitations require your
              own account. Reload/reset affects only this example. Changing the stage opens another
              prepared example. The fictional teacher’s judgement is not a promised learning result.
            </p>
            <div className="mt-2 flex flex-wrap gap-4">
              <Link
                className="font-bold text-ink underline"
                to={`/try/lms/${profile}/${perspective === 'student' ? 'family' : 'student'}`}
              >
                {perspective === 'student'
                  ? 'Read prepared family report'
                  : 'Read prepared student work'}
              </Link>
              {teacherOrigin && (
                <a
                  className="font-bold text-ink underline"
                  href={`${teacherOrigin}/demo/${profile}/lms`}
                >
                  Back to teacher demo ↗
                </a>
              )}
            </div>
          </details>
        </section>
        <DemoContent
          key={`${profile}-${state}-${perspective}-${practice}`}
          perspective={perspective}
          request={request}
        />
      </main>
      <footer
        aria-label="Exit demo mode"
        className="fixed inset-x-0 bottom-0 z-[60] flex min-h-20 items-center justify-between gap-3 border-t-2 border-brand-sky bg-wash-sky px-5 py-3 sm:px-8"
      >
        <div>
          <p className="text-sm font-bold text-ink">You are exploring a demo</p>
          <p className="text-xs text-slate2">Your own learning stays separate.</p>
        </div>
        <a href={formalPath} className="btn-pill-primary shrink-0 !px-5 !py-3 !text-sm">
          Exit demo
        </a>
      </footer>
    </div>
  );
}

function DemoContent({
  perspective,
  request,
}: {
  perspective: string;
  request: ReturnType<typeof createLmsDemoApi>;
}) {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { retry: false, gcTime: 0 }, mutations: { retry: false } },
      }),
  );
  return (
    <QueryClientProvider client={client}>
      {perspective === 'family' ? (
        <SchoolLearningPage request={request} />
      ) : (
        <>
          <LmsAssignmentResponse
            assignmentId="demo-lms-assignment-1"
            kidId="demo-student-1"
            request={request}
          />
          <IndividualLearningPlans request={request} kidId="demo-student-1" />
        </>
      )}
    </QueryClientProvider>
  );
}
